import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import AdminPanel from '../components/AdminPanel';

const AdminLogin = ({ siteData, updateSiteData, setSiteData }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loginLoading, setLoginLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setUser(user);
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginLoading(true);
        setError('');

        try {
            await signInWithEmailAndPassword(auth, email, password);
            // Success - user state will be updated by the onAuthStateChanged listener
        } catch (error) {
            let errorMessage = 'Login failed. Please check your credentials.';

            switch (error.code) {
                case 'auth/user-not-found':
                    errorMessage = 'No admin account found with this email address.';
                    break;
                case 'auth/wrong-password':
                    errorMessage = 'Incorrect password.';
                    break;
                case 'auth/invalid-email':
                    errorMessage = 'Invalid email address.';
                    break;
                case 'auth/too-many-requests':
                    errorMessage = 'Too many failed attempts. Please try again later.';
                    break;
                case 'auth/invalid-credential':
                    errorMessage = 'Invalid login credentials. Please check your email and password.';
                    break;
                default:
                    errorMessage = 'Login failed. Please try again.';
            }

            setError(errorMessage);
        } finally {
            setLoginLoading(false);
        }
    };

    const handleBackToWebsite = () => {
        navigate('/');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-stone-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-stone-800 mx-auto mb-4"></div>
                    <p className="text-stone-600">Loading...</p>
                </div>
            </div>
        );
    }

    // If user is authenticated, show the admin panel
    if (user) {
        return (
            <div className="min-h-screen bg-stone-100">
                {/* Admin Header */}
                <div className="bg-stone-900 text-white px-4 py-3 flex justify-between items-center">
                    <div>
                        <h1 className="text-lg font-semibold">Admin Panel</h1>
                        <p className="text-stone-300 text-sm">Logged in as: {user.email}</p>
                    </div>
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={handleBackToWebsite}
                            className="text-sm bg-stone-700 hover:bg-stone-600 px-3 py-1 rounded transition-colors"
                        >
                            View Website
                        </button>
                        <button
                            onClick={() => auth.signOut()}
                            className="text-sm bg-red-600 hover:bg-red-500 px-3 py-1 rounded transition-colors"
                        >
                            Logout
                        </button>
                    </div>
                </div>

                {/* Admin Panel */}
                <AdminPanel
                    siteData={siteData}
                    updateSiteData={updateSiteData}
                    setSiteData={setSiteData}
                />
            </div>
        );
    }

    // Show login form
    return (
        <div className="min-h-screen bg-stone-100 flex items-center justify-center py-12 px-4">
            <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-stone-800 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-stone-800">Admin Login</h2>
                    <p className="text-stone-600 mt-2">Sign in to access the admin panel</p>
                </div>

                <form onSubmit={handleLogin} className="space-y-6">
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-stone-700 mb-2">
                            Email Address
                        </label>
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-colors"
                            placeholder="admin@example.com"
                            required
                            autoComplete="email"
                        />
                    </div>

                    <div>
                        <label htmlFor="password" className="block text-sm font-medium text-stone-700 mb-2">
                            Password
                        </label>
                        <input
                            type="password"
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-colors"
                            placeholder="Enter your password"
                            required
                            autoComplete="current-password"
                        />
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loginLoading}
                        className="w-full bg-stone-800 text-white py-3 px-4 rounded-lg font-medium hover:bg-stone-700 focus:ring-2 focus:ring-stone-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loginLoading ? (
                            <div className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Signing in...
                            </div>
                        ) : (
                            'Sign In'
                        )}
                    </button>
                </form>

                <div className="mt-8 text-center">
                    <button
                        onClick={handleBackToWebsite}
                        className="text-stone-600 hover:text-stone-800 text-sm font-medium transition-colors"
                    >
                        ← Back to Website
                    </button>
                </div>

                <div className="mt-6 text-center">
                    <p className="text-xs text-stone-500">
                        This is a secure admin area. Only authorized personnel can access this panel.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default AdminLogin;